import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { empresasApi } from '../services/empresasApiService';
import { toast } from 'react-hot-toast';

// Context simple
const SimpleEmpresaContext = createContext();

// Estado inicial simple
const INITIAL_STATE = {
  empresaActual: null,
  empresasDisponibles: [],
  usuarioEmpresa: null,
  loading: false,
  error: null,
  planActual: null,
  limitesUsage: null,
  isSuperAdmin: false,
  isAdmin: false,
  isMecanico: false,
  isCliente: false,
  inicializado: false
};

// Control global para evitar requests duplicados y cache persistente
let globalLoadingState = {
  isLoading: false,
  loadingPromise: null
};

// Cache persistente para evitar re-inicializaciones
const EMPRESA_CACHE = {
  data: null,
  userId: null,
  timestamp: null,
  isValid: function(userId) {
    const now = Date.now();
    const cacheAge = now - (this.timestamp || 0);
    const maxAge = 5 * 60 * 1000; // 5 minutos
    return this.userId === userId && this.data && cacheAge < maxAge;
  },
  set: function(userId, data) {
    this.userId = userId;
    this.data = data;
    this.timestamp = Date.now();
  },
  clear: function() {
    this.data = null;
    this.userId = null;
    this.timestamp = null;
  }
};

// Provider simple que maneja todo
export const SimpleEmpresaProvider = ({ children }) => {
  const [state, setState] = useState(INITIAL_STATE);
  const { user, isLoaded } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  // Función para actualizar estado parcialmente
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Cargar empresas del usuario
  const cargarEmpresas = useCallback(async () => {
    if (!user?.id) {
      updateState({ loading: false, inicializado: true });
      return;
    }

    // Skip durante onboarding y rutas públicas específicas
    const isOnboardingRoute = location.pathname.includes('/onboarding') || 
                             location.pathname.includes('/taller') || 
                             location.pathname === '/cliente' || // Solo la página pública de landing
                             location.pathname.startsWith('/select-') ||
                             location.pathname.startsWith('/solicitar-');
                             
    if (isOnboardingRoute) {
      console.log('🚫 [useSimpleEmpresa] Skip carga - ruta onboarding/landing:', location.pathname);
      updateState({ loading: false, inicializado: true });
      return;
    }

    // Usar cache si es válido para evitar re-requests
    if (EMPRESA_CACHE.isValid(user.id)) {
      console.log('📋 [useSimpleEmpresa] Usando cache válido, evitando API call');
      const cachedData = EMPRESA_CACHE.data;
      updateState({
        ...cachedData,
        loading: false,
        inicializado: true
      });
      return;
    }

    // Evitar requests duplicados
    if (globalLoadingState.isLoading) {
      console.log('⚠️ [useSimpleEmpresa] Request ya en progreso, esperando...');
      if (globalLoadingState.loadingPromise) {
        try {
          await globalLoadingState.loadingPromise;
        } catch (error) {
          // Error ya manejado en el request original
        }
      }
      return;
    }

    try {
      console.log('🔄 [useSimpleEmpresa] Iniciando carga de empresas para usuario:', user.id);
      globalLoadingState.isLoading = true;
      updateState({ loading: true, error: null });

      // Crear la promesa y guardarla globalmente
      const loadingPromise = empresasApi.obtenerEmpresasUsuario();
      globalLoadingState.loadingPromise = loadingPromise;
      
      const empresas = await loadingPromise;
      console.log('📋 [useSimpleEmpresa] Empresas obtenidas:', empresas.length);
      
      if (empresas.length === 0) {
        // Sin empresas - determinar redirección
        const userType = localStorage.getItem('revvly_user_type');
        if (userType === 'taller') {
          navigate('/onboarding/empresa');
        } else if (userType === 'cliente') {
          navigate('/solicitar-acceso');
        } else {
          navigate('/select-user-type');
        }
        updateState({ loading: false, inicializado: true, empresasDisponibles: [] });
        globalLoadingState.isLoading = false;
        globalLoadingState.loadingPromise = null;
        return;
      }

      const isSuperAdmin = empresas.some(e => e.es_superadmin);
      
      // Seleccionar empresa (guardada o primera)
      let empresaActual = null;
      const empresaGuardada = localStorage.getItem('revvly_empresa_actual');
      if (empresaGuardada) {
        try {
          const { id } = JSON.parse(empresaGuardada);
          empresaActual = empresas.find(e => e.id === id) || empresas[0];
        } catch {
          empresaActual = empresas[0];
        }
      } else {
        empresaActual = empresas[0];
      }

      // Determinar rol
      const perfilId = empresaActual.perfil_id || (isSuperAdmin ? 'admin' : 'usuario');
      const isAdmin = perfilId === 'admin' || isSuperAdmin;
      const isMecanico = perfilId === 'mecanico';
      const isCliente = perfilId === 'cliente';

      const newState = {
        empresasDisponibles: empresas,
        empresaActual,
        usuarioEmpresa: empresaActual,
        isSuperAdmin,
        isAdmin,
        isMecanico,
        isCliente,
        loading: false,
        inicializado: true
      };

      updateState(newState);

      // Guardar en cache para evitar re-requests
      EMPRESA_CACHE.set(user.id, newState);

      // Guardar empresa seleccionada
      localStorage.setItem('revvly_empresa_actual', JSON.stringify({
        id: empresaActual.id,
        nombre: empresaActual.nombre
      }));

    } catch (error) {
      console.error('❌ [useSimpleEmpresa] Error cargando empresas:', error);
      updateState({ error: error.message, loading: false, inicializado: true });
      // Limpiar cache en caso de error
      EMPRESA_CACHE.clear();
    } finally {
      // Limpiar estado global
      globalLoadingState.isLoading = false;
      globalLoadingState.loadingPromise = null;
    }
  }, [user?.id, navigate, updateState]); // Remover location.pathname para evitar re-renders

  // Cambiar empresa
  const cambiarEmpresa = useCallback(async (empresaId) => {
    const empresa = state.empresasDisponibles.find(e => e.id === empresaId);
    if (!empresa) return;

    try {
      updateState({ loading: true });
      
      const isAdmin = empresa.perfil_id === 'admin' || state.isSuperAdmin;
      const isMecanico = empresa.perfil_id === 'mecanico';
      const isCliente = empresa.perfil_id === 'cliente';
      
      updateState({
        empresaActual: empresa,
        usuarioEmpresa: empresa,
        isAdmin,
        isMecanico,
        isCliente,
        loading: false
      });

      localStorage.setItem('revvly_empresa_actual', JSON.stringify({
        id: empresa.id,
        nombre: empresa.nombre
      }));

      toast.success(`Cambiado a ${empresa.nombre}`);
    } catch (error) {
      updateState({ error: error.message, loading: false });
      toast.error('Error al cambiar empresa');
    }
  }, [state.empresasDisponibles, state.isSuperAdmin, updateState]);

  // Crear nueva empresa
  const crearEmpresa = useCallback(async (empresaData) => {
    try {
      updateState({ loading: true });
      
      const nuevaEmpresa = await empresasApi.crearEmpresa(empresaData, user?.id);
      
      // Configurar como admin de la nueva empresa
      const empresaConPerfil = {
        ...nuevaEmpresa,
        perfil_id: 'admin',
        es_admin: true
      };

      updateState({
        empresasDisponibles: [empresaConPerfil],
        empresaActual: empresaConPerfil,
        usuarioEmpresa: empresaConPerfil,
        isAdmin: true,
        loading: false
      });

      // Limpiar flags de onboarding
      localStorage.removeItem('revvly_user_type');
      localStorage.removeItem('onboarding_completed');
      localStorage.removeItem('sucursalData');

      localStorage.setItem('revvly_empresa_actual', JSON.stringify({
        id: empresaConPerfil.id,
        nombre: empresaConPerfil.nombre
      }));

      return nuevaEmpresa;
    } catch (error) {
      updateState({ error: error.message, loading: false });
      throw error;
    }
  }, [user?.id, updateState]);

  // Unirse a empresa por invitación
  const unirseEmpresa = useCallback(async (codigoInvitacion) => {
    try {
      updateState({ loading: true });
      
      await empresasApi.joinEmpresaByInvitation(codigoInvitacion);
      
      // Recargar empresas después de unirse
      await cargarEmpresas();
      
      updateState({ loading: false });
    } catch (error) {
      updateState({ error: error.message, loading: false });
      throw error;
    }
  }, [cargarEmpresas, updateState]);

  // Efectos de inicialización - solo cuando cambie el usuario
  useEffect(() => {
    if (isLoaded && user) {
      cargarEmpresas();
    } else if (isLoaded && !user) {
      // Reset si no hay usuario
      setState(INITIAL_STATE);
      localStorage.removeItem('revvly_empresa_actual');
      EMPRESA_CACHE.clear();
    }
  }, [isLoaded, user?.id]); // Remover cargarEmpresas para evitar loops

  // Funciones de permisos simples
  const verificarPermiso = useCallback((recurso, accion) => {
    if (state.isSuperAdmin || state.isAdmin) return true;
    return false; // Simplificado por ahora
  }, [state.isSuperAdmin, state.isAdmin]);

  // Valor del contexto
  const contextValue = {
    ...state,
    
    // Funciones principales
    cargarEmpresas,
    cambiarEmpresa,
    crearEmpresa,
    unirseEmpresa,
    verificarPermiso,
    
    // Helpers
    canEdit: (recurso) => verificarPermiso(recurso, 'editar'),
    canCreate: (recurso) => verificarPermiso(recurso, 'crear'),
    canDelete: (recurso) => verificarPermiso(recurso, 'eliminar'),
    canView: (recurso) => verificarPermiso(recurso, 'ver'),
    
    // Props computadas
    hasCurrentEmpresa: !!state.empresaActual,
    needsOnboarding: state.empresasDisponibles.length === 0 && !state.loading && state.inicializado,
    
    // Compatibilidad
    setEmpresaActual: cambiarEmpresa,
    seleccionarEmpresa: cambiarEmpresa,
    loadingCambioEmpresa: state.loading,
    currentEmpresa: state.empresaActual,
    userEmpresas: state.empresasDisponibles,
    isContextReady: state.inicializado && !!state.empresaActual
  };

  return (
    <SimpleEmpresaContext.Provider value={contextValue}>
      {children}
    </SimpleEmpresaContext.Provider>
  );
};

// Hook para usar el contexto
export const useSimpleEmpresa = () => {
  const context = useContext(SimpleEmpresaContext);
  if (!context) {
    throw new Error('useSimpleEmpresa debe usarse dentro de SimpleEmpresaProvider');
  }
  return {
    ...context,
    isInitialized: context.empresaActual !== null || context.inicializado
  };
};

export default useSimpleEmpresa;